import { Inject, Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { ContentContainerStore } from '@app/core/containers/content-container/store/content-container.store';
import { MenuService } from '@app/core/services/menu/menu.service';
import { PermissionService } from '@app/core/services/permission/permission.service';
import { Icons } from '@app/shared/constants';
import { MenuItem } from '@app/shared/models/menu.interface';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, timer } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

const MIN_MENU_FOR_SU: MenuItem[] = [
  {
    title: 'Menu manager',
    tx: 'core.menu.entry.menuManager',
    value: '',
    icon: Icons.MENU_EDIT,
    link: '/menu',
    level: 0,
    external: false,
    children: [] as MenuItem[]
  },
  {
    title: 'Rights manager',
    tx: 'core.menu.entry.rightsManager',
    value: '',
    icon: Icons.LOCK,
    link: '/right/user',
    level: 0,
    external: false,
    children: [] as MenuItem[]
  }
];

@Injectable({
  providedIn: 'root'
})
export class MenuResolverService implements Resolve<MenuItem[]> {
  protected readonly REFRESH_INTERVAL = 60000000;
  protected readonly CACHE_SIZE = 1;
  protected reload$ = new Subject<void>();
  protected cache$: Observable<MenuItem[]>;

  constructor(
    @Inject('ContentContainerStore') private store: ContentContainerStore,
    private menuService: MenuService,
    private permissionService: PermissionService,
    private translateService: TranslateService
  ) {}

  resolve(): Observable<MenuItem[]> {
    if (!this.cache$) {
      this.cache$ = timer(0, this.REFRESH_INTERVAL).pipe(
        switchMap(_ => this.retrieveMenu()),
        map(items => {
          if (items.length === 0 && this.permissionService.isSuperuser()) {
            console.warn('No default menu found for superuser, returning the minimum one');
            return MIN_MENU_FOR_SU.map(me => ({ ...me, value: this.translateService.instant(me.tx) }));
          } else {
            return items;
          }
        }),
        takeUntil(this.reload$),
        shareReplay(this.CACHE_SIZE),
        take(1)
      );
    }
    return this.cache$;
  }

  evictCache(): void {
    this.reload$.next();
    this.cache$ = null;
  }

  protected retrieveMenu(): Observable<MenuItem[]> {
    return this.menuService.getMenuForLoggedInUser();
  }
}
